import React, { useState } from 'react';
import { Modal } from '../ui/Modal';
interface TradeActivity {
  type: 'buy' | 'sell';
  asset: string;
  amount: string;
  price: string;
  time: string;
  date: string;
  orderId?: string;
  leverage?: string;
  market?: string;
  orderType?: string;
  orderState?: string;
  reduceOnly?: boolean;
  timeInForce?: string;
}
interface TransactionModalProps {
  isOpen: boolean;
  onClose: () => void;
  transaction: TradeActivity | null;
}
export function TransactionModal({
  isOpen,
  onClose,
  transaction
}: TransactionModalProps) {
  const [activeTab, setActiveTab] = useState<'terms' | 'executions' | 'trades'>('terms');
  if (!transaction) return null;
  const isBuy = transaction.type === 'buy';
  // Mock data for executions tab
  const executionsData = {
    quantityExecuted: transaction.amount,
    cost: '105.92015',
    averagePrice: transaction.price,
    fee: '0.42368',
    opened: `${transaction.date} ${transaction.time}`,
    closed: `${transaction.date} ${transaction.time}`
  };
  // Mock data for trades tab
  const tradesData = [{
    date: `12/12/25`,
    time: '4:18 PM',
    side: transaction.type,
    type: 'Market',
    price: transaction.price,
    volume: '0.00034516',
    cost: '31.68'
  }, {
    date: `12/12/25`,
    time: '4:18 PM',
    side: transaction.type,
    type: 'Market',
    price: transaction.price,
    volume: '0.000809',
    cost: '74.24'
  }];
  return <Modal isOpen={isOpen} onClose={onClose} maxWidth="2xl" data-id="element-975">
      {/* Header with asset info */}
      <div className="px-6 py-6 border-b border-border-subtle" data-id="element-976">
        <div className="flex items-center gap-3 mb-4" data-id="element-977">
          <div className="w-12 h-12 rounded-full bg-warning/20 flex items-center justify-center" data-id="element-978">
            <span className="text-2xl" data-id="element-979">₿</span>
          </div>
          <div data-id="element-980">
            <h2 className="text-xl font-semibold text-text-primary" data-id="element-981">
              {transaction.asset}/USD
            </h2>
          </div>
        </div>

        <div className={`text-lg font-medium ${isBuy ? 'text-success' : 'text-danger'}`} data-id="element-982">
          {isBuy ? 'Buy' : 'Sell'} {transaction.amount} {transaction.asset} @{' '}
          {isBuy ? 'Limit' : 'Market'} {transaction.price} USD
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-2 px-6 py-4 border-b border-border-subtle" data-id="element-983">
        <button onClick={() => setActiveTab('terms')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === 'terms' ? 'bg-background-secondary text-text-primary' : 'text-text-secondary hover:text-text-primary hover:bg-background-secondary/50'}`} data-id="element-984">
          Terms
        </button>
        <button onClick={() => setActiveTab('executions')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === 'executions' ? 'bg-background-secondary text-text-primary' : 'text-text-secondary hover:text-text-primary hover:bg-background-secondary/50'}`} data-id="element-985">
          Executions
        </button>
        <button onClick={() => setActiveTab('trades')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === 'trades' ? 'bg-background-secondary text-text-primary' : 'text-text-secondary hover:text-text-primary hover:bg-background-secondary/50'}`} data-id="element-986">
          Trades
        </button>
      </div>

      {/* Content */}
      <div className="px-6 py-6" data-id="element-987">
        {activeTab === 'terms' && <div className="space-y-4" data-id="element-988">
            {/* Order state */}
            <div className="flex items-center justify-between py-3" data-id="element-989">
              <span className="text-sm text-text-secondary" data-id="element-990">Order state</span>
              <div className="inline-flex items-center px-3 py-1 rounded-full bg-success/10 text-success text-sm font-medium" data-id="element-991">
                Filled
              </div>
            </div>

            {/* Order type */}
            <div className="flex items-center justify-between py-3" data-id="element-992">
              <span className="text-sm text-text-secondary" data-id="element-993">Order type</span>
              <span className="text-base font-medium text-text-primary" data-id="element-994">
                {isBuy ? 'Buy' : 'Sell'} – Limit
              </span>
            </div>

            {/* Leverage */}
            <div className="flex items-center justify-between py-3" data-id="element-995">
              <span className="text-sm text-text-secondary" data-id="element-996">Leverage</span>
              <span className="text-base font-medium text-text-primary" data-id="element-997">
                10x
              </span>
            </div>

            {/* Market */}
            <div className="flex items-center justify-between py-3" data-id="element-998">
              <span className="text-sm text-text-secondary" data-id="element-999">Market</span>
              <span className="text-base font-medium text-text-primary" data-id="element-1000">
                {transaction.asset}
                <span className="text-text-secondary" data-id="element-1001">/USD</span>
              </span>
            </div>

            {/* Price */}
            <div className="flex items-center justify-between py-3" data-id="element-1002">
              <span className="text-sm text-text-secondary" data-id="element-1003">Price</span>
              <span className="text-base font-medium font-numbers text-text-primary" data-id="element-1004">
                {transaction.price}{' '}
                <span className="text-text-secondary text-sm" data-id="element-1005">USD</span>
              </span>
            </div>

            {/* Reduce only */}
            <div className="flex items-center justify-between py-3" data-id="element-1006">
              <span className="text-sm text-text-secondary" data-id="element-1007">Reduce only</span>
              <span className="text-base font-medium text-text-primary" data-id="element-1008">
                No
              </span>
            </div>

            {/* Order ID */}
            <div className="flex items-center justify-between py-3 pt-6 border-t border-border-subtle" data-id="element-1009">
              <span className="text-sm text-text-secondary" data-id="element-1010">Order ID</span>
              <span className="text-sm font-mono text-text-primary" data-id="element-1011">
                OWY5CU-M6VZX-UHUHZ4
              </span>
            </div>
          </div>}

        {activeTab === 'executions' && <div className="space-y-4" data-id="element-1012">
            {/* Quantity executed */}
            <div className="flex items-center justify-between py-3" data-id="element-1013">
              <span className="text-sm text-text-secondary" data-id="element-1014">
                Quantity executed
              </span>
              <span className="text-base font-medium font-numbers text-text-primary" data-id="element-1015">
                {executionsData.quantityExecuted}{' '}
                <span className="text-text-secondary text-sm" data-id="element-1016">
                  {transaction.asset}
                </span>
              </span>
            </div>

            {/* Cost */}
            <div className="flex items-center justify-between py-3" data-id="element-1017">
              <span className="text-sm text-text-secondary" data-id="element-1018">Cost</span>
              <span className="text-base font-medium font-numbers text-text-primary" data-id="element-1019">
                {executionsData.cost}{' '}
                <span className="text-text-secondary text-sm" data-id="element-1020">USD</span>
              </span>
            </div>

            {/* Average price */}
            <div className="flex items-center justify-between py-3" data-id="element-1021">
              <span className="text-sm text-text-secondary" data-id="element-1022">Average price</span>
              <span className="text-base font-medium font-numbers text-text-primary" data-id="element-1023">
                {executionsData.averagePrice}{' '}
                <span className="text-text-secondary text-sm" data-id="element-1024">USD</span>
              </span>
            </div>

            {/* Opened */}
            <div className="flex items-center justify-between py-3" data-id="element-1025">
              <span className="text-sm text-text-secondary" data-id="element-1026">Opened</span>
              <span className="text-base font-medium text-text-primary" data-id="element-1027">
                {executionsData.opened}
              </span>
            </div>

            {/* Fee */}
            <div className="flex items-center justify-between py-3" data-id="element-1028">
              <span className="text-sm text-text-secondary" data-id="element-1029">Fee</span>
              <span className="text-base font-medium font-numbers text-text-primary" data-id="element-1030">
                {executionsData.fee}{' '}
                <span className="text-text-secondary text-sm" data-id="element-1031">USD</span>
              </span>
            </div>

            {/* Closed */}
            <div className="flex items-center justify-between py-3" data-id="element-1032">
              <span className="text-sm text-text-secondary" data-id="element-1033">Closed</span>
              <span className="text-base font-medium text-text-primary" data-id="element-1034">
                {executionsData.closed}
              </span>
            </div>
          </div>}

        {activeTab === 'trades' && <div className="overflow-x-auto -mx-6 px-6" data-id="element-1035">
            <table className="w-full" data-id="element-1036">
              <thead data-id="element-1037">
                <tr className="border-b border-border-subtle" data-id="element-1038">
                  <th className="text-left py-3 px-2 text-xs font-medium text-text-secondary uppercase tracking-wider" data-id="element-1039">
                    Date
                  </th>
                  <th className="text-left py-3 px-2 text-xs font-medium text-text-secondary uppercase tracking-wider" data-id="element-1040">
                    Side
                  </th>
                  <th className="text-left py-3 px-2 text-xs font-medium text-text-secondary uppercase tracking-wider" data-id="element-1041">
                    Type
                  </th>
                  <th className="text-right py-3 px-2 text-xs font-medium text-text-secondary uppercase tracking-wider" data-id="element-1042">
                    Price
                  </th>
                  <th className="text-right py-3 px-2 text-xs font-medium text-text-secondary uppercase tracking-wider" data-id="element-1043">
                    Volume
                  </th>
                  <th className="text-right py-3 px-2 text-xs font-medium text-text-secondary uppercase tracking-wider" data-id="element-1044">
                    Cost
                  </th>
                </tr>
              </thead>
              <tbody data-id="element-1045">
                {tradesData.map((trade, idx) => <tr key={idx} className="border-b border-border-subtle last:border-0" data-id="element-1046">
                    <td className="py-4 px-2" data-id="element-1047">
                      <div className="text-sm text-text-primary" data-id="element-1048">
                        {trade.date}
                      </div>
                      <div className="text-xs text-text-secondary" data-id="element-1049">
                        {trade.time}
                      </div>
                    </td>
                    <td className="py-4 px-2" data-id="element-1050">
                      <div className={`text-sm font-medium capitalize ${trade.side === 'buy' ? 'text-success' : 'text-danger'}`} data-id="element-1051">
                        {trade.side}
                      </div>
                    </td>
                    <td className="py-4 px-2" data-id="element-1052">
                      <div className="text-sm text-text-primary" data-id="element-1053">
                        {trade.type}
                      </div>
                    </td>
                    <td className="py-4 px-2 text-right" data-id="element-1054">
                      <div className="text-sm font-numbers text-text-primary" data-id="element-1055">
                        {trade.price}{' '}
                        <span className="text-text-secondary text-xs" data-id="element-1056">USD</span>
                      </div>
                    </td>
                    <td className="py-4 px-2 text-right" data-id="element-1057">
                      <div className="text-sm font-numbers text-text-primary" data-id="element-1058">
                        {trade.volume}{' '}
                        <span className="text-text-secondary text-xs" data-id="element-1059">
                          {transaction.asset}
                        </span>
                      </div>
                    </td>
                    <td className="py-4 px-2 text-right" data-id="element-1060">
                      <div className="text-sm font-numbers text-text-primary" data-id="element-1061">
                        {trade.cost}{' '}
                        <span className="text-text-secondary text-xs" data-id="element-1062">USD</span>
                      </div>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>}
      </div>
    </Modal>;
}